<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  invoice: {
    type: Object,
    required: true,
  },
  isPaying: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['pay']);

const { t } = useI18n();

const formatDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
};

const formatPeriod = (start, end) => {
  if (!start || !end) return null;
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  const startLabel = new Intl.DateTimeFormat(undefined, opts).format(s);
  const endLabel = new Intl.DateTimeFormat(undefined, opts).format(e);
  return `${startLabel} – ${endLabel}`;
};

const money = amount => {
  const symbol = props.invoice.currency_symbol || '';
  const formatted = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
};

const moneyWithCode = amount =>
  `${money(amount)} ${props.invoice.currency_code}`;

const periodLabel = computed(() =>
  formatPeriod(props.invoice.period_start, props.invoice.period_end)
);

const amountDueHeadline = computed(() => {
  if (props.invoice.status === 'paid') {
    return t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_PAID_ON', {
      amount: moneyWithCode(props.invoice.total),
      date: formatDate(props.invoice.paid_at),
    });
  }
  return t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_AMOUNT_DUE', {
    amount: moneyWithCode(props.invoice.total),
    date: formatDate(props.invoice.due_date),
  });
});

const issuer = computed(() => props.invoice.issuer || {});
const billTo = computed(() => props.invoice.bill_to || {});

const lineItems = computed(() => props.invoice.line_items || []);

const showPayButton = computed(
  () => props.invoice.payable && props.invoice.status !== 'paid'
);

const billingReasonLabel = computed(() => {
  const key = props.invoice.billing_reason || 'plan_renewal';
  const path = `GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_BILLING_REASON.${key}`;
  const label = t(path);
  return label === path ? key.replace(/_/g, ' ') : label;
});

const issuedByLabel = computed(() => {
  const key = props.invoice.issued_by || 'system';
  const path = `GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_ISSUED_BY.${key}`;
  const label = t(path);
  return label === path ? key : label;
});
</script>

<template>
  <article class="flex flex-col gap-8 text-n-slate-12">
    <header
      class="grid gap-8 border-0 border-b border-solid border-n-weak pb-8 md:grid-cols-[1fr_1fr_1fr]"
    >
      <div class="flex flex-col gap-1">
        <h2 class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_DOC_TITLE') }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">
          <span class="text-n-slate-11">
            {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_NUMBER') }}
          </span>
          {{ invoice.invoice_number || `#${invoice.id}` }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">
          <span class="text-n-slate-11">
            {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_ISSUED') }}
          </span>
          {{ formatDate(invoice.issued_at) }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">
          <span class="text-n-slate-11">
            {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_DUE') }}
          </span>
          {{ formatDate(invoice.due_date) }}
        </p>
        <p v-if="invoice.billing_reason" class="mb-0 text-sm text-n-slate-11">
          <span class="text-n-slate-11">
            {{
              t(
                'GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_DOC_BILLING_REASON'
              )
            }}:
          </span>
          {{ billingReasonLabel }}
        </p>
        <p v-if="invoice.issued_by" class="mb-0 text-sm text-n-slate-11">
          <span class="text-n-slate-11">
            {{
              t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_DOC_ISSUED_BY')
            }}:
          </span>
          {{ issuedByLabel }}
        </p>
      </div>

      <div class="flex flex-col gap-0.5 text-sm text-n-slate-12">
        <p v-if="issuer.name" class="mb-0 font-semibold">
          {{ issuer.name }}
        </p>
        <p
          v-for="(line, idx) in issuer.address_lines || []"
          :key="`addr-${idx}`"
          class="mb-0 text-n-slate-11"
        >
          {{ line }}
        </p>
        <p v-if="issuer.email" class="mb-0 text-n-slate-11">
          {{ issuer.email }}
        </p>
        <p v-if="issuer.tax_id" class="mb-0 text-n-slate-11">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_TAX_ID') }}
          {{ issuer.tax_id }}
        </p>
      </div>

      <div class="flex flex-col gap-1 text-sm">
        <p class="mb-0 font-semibold text-n-slate-12">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_BILL_TO') }}
        </p>
        <p v-if="billTo.name" class="mb-0 text-n-slate-12">
          {{ billTo.name }}
        </p>
        <p v-if="billTo.organization" class="mb-0 text-n-slate-12">
          {{ billTo.organization }}
        </p>
        <p v-if="billTo.country" class="mb-0 text-n-slate-11">
          {{ billTo.country }}
        </p>
        <p v-if="billTo.email" class="mb-0 text-n-slate-11">
          {{ billTo.email }}
        </p>
      </div>
    </header>

    <div
      class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <p class="mb-0 text-xl font-semibold tabular-nums text-n-slate-12">
        {{ amountDueHeadline }}
      </p>
      <Button
        v-if="showPayButton"
        size="md"
        :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_PAY_ONLINE')"
        :is-loading="isPaying"
        class="w-full shrink-0 sm:w-auto"
        @click="emit('pay', invoice)"
      />
    </div>

    <div class="overflow-x-auto">
      <table class="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr
            class="border-b border-solid border-n-weak text-xs text-n-slate-11"
          >
            <th class="py-2 pe-4 text-start font-medium">
              {{
                t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_DESCRIPTION')
              }}
            </th>
            <th class="w-16 py-2 pe-4 text-end font-medium">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_QTY') }}
            </th>
            <th class="w-28 py-2 pe-4 text-end font-medium">
              {{
                t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_UNIT_PRICE')
              }}
            </th>
            <th class="w-28 py-2 text-end font-medium">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_AMOUNT') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in lineItems"
            :key="item.id"
            class="border-b border-solid border-n-weak align-top"
          >
            <td class="py-4 pe-4">
              <p class="mb-0 font-medium text-n-slate-12">
                {{ item.description }}
              </p>
              <p v-if="periodLabel" class="mb-0 mt-1 text-xs text-n-slate-11">
                {{ periodLabel }}
              </p>
            </td>
            <td class="py-4 pe-4 text-end tabular-nums text-n-slate-12">
              {{ item.quantity }}
            </td>
            <td class="py-4 pe-4 text-end tabular-nums text-n-slate-12">
              {{ money(item.unit_price) }}
            </td>
            <td class="py-4 text-end tabular-nums font-medium text-n-slate-12">
              {{ money(item.total) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex flex-col items-end gap-1 text-sm">
      <div class="flex w-full max-w-xs justify-between gap-8">
        <span class="text-n-slate-11">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_SUBTOTAL') }}
        </span>
        <span class="tabular-nums text-n-slate-12">{{
          money(invoice.subtotal)
        }}</span>
      </div>
      <div
        v-if="invoice.discount_amount > 0"
        class="flex w-full max-w-xs justify-between gap-8"
      >
        <span class="text-n-slate-11">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_DISCOUNT') }}
        </span>
        <span class="tabular-nums text-n-slate-12">
          -{{ money(invoice.discount_amount) }}
        </span>
      </div>
      <div class="flex w-full max-w-xs justify-between gap-8 font-semibold">
        <span class="text-n-slate-12">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_TOTAL') }}
        </span>
        <span class="tabular-nums text-n-slate-12">{{
          money(invoice.total)
        }}</span>
      </div>
      <div
        class="flex w-full max-w-xs justify-between gap-8 border-t border-solid border-n-weak pt-2 font-semibold"
      >
        <span class="text-n-slate-12">
          {{
            t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_AMOUNT_DUE_LABEL')
          }}
        </span>
        <span class="tabular-nums text-n-slate-12">
          {{ moneyWithCode(invoice.total) }}
        </span>
      </div>
    </div>

    <p
      v-if="invoice.footer_note"
      class="mb-0 border-0 border-t border-solid border-n-weak pt-6 text-xs leading-relaxed text-n-slate-11"
    >
      {{ invoice.footer_note }}
    </p>
  </article>
</template>
